import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PatientLoader from "../../common/PatientLoader";
import ChatTile from "./ChatTile";
import NoMessage from "./NoMessage";
import StartChat from "./StartChatModal";
import { ReactComponent as SearchIcon } from "../../../assets/icons/Search.svg";
import { ReactComponent as PlusCircleIcon } from "../../../assets/icons/plus-circle.svg";
import { updateMessageInSidebar } from "./AgoraMethods";
import { isEmptyObject } from "jquery";
import { getUnreadMessagesLogic, getChatListingLogic } from "../../../store/features/chat/chatSlice";

const ChatListing = ({
  chatPatients,
  setChatData,
  chatEnabledPatients,
  latestMessage,
  setChatPatients,
}) => {
  const [filterText, setFilterText] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(1);

  const { chatListingLoading: chatLoading, chatListing, UnreadPatientsChatCount } = useSelector((state) => state.chat);
  const { user, selectedClinic, selectedProvider } = useSelector((state) => state.app);
  const dispatch = useDispatch();

  const clinicianId = selectedProvider ? selectedProvider : user?.id;
  const clinicId = selectedClinic?.clinic?.id;

  const isFetchingRef = useRef(false);

  // Compute total pages from the API count — deterministic, can't be raced
  const PAGE_SIZE = 10;
  const totalPages = Math.ceil((chatListing?.count || 0) / PAGE_SIZE);
  const hasMore = page < totalPages;

  // Effect 1: Unlock scroll lock and append results once the API responds
  useEffect(() => {
    if (!chatLoading && !isEmptyObject(chatListing)) {
      isFetchingRef.current = false; // unlock AFTER api responds — not before
      setChatPatients((prevPatients) => {
        // page 1 always replaces the list (new search or initial load)
        if (prevPatients.length === 0 || page === 1) {
          const results = chatListing?.results || [];
          const openChatID = sessionStorage.getItem("openChatID");
          if (openChatID) {
            const hasOpenChat = results.some((p) => String(p?.patient_id) === String(openChatID));
            if (!hasOpenChat) {
              const openPatient = prevPatients.find((p) => String(p?.patient_id) === String(openChatID));
              if (openPatient) {
                return [openPatient, ...results];
              }
            }
          }
          return results;
        }
        // For subsequent pages, append and deduplicate
        const newResults = chatListing?.results?.filter(
          (newChat) => !prevPatients.some((p) => p?.patient_id === newChat.patient_id)
        ) || [];
        return [...prevPatients, ...newResults];
      });
    }
  }, [chatListing, chatLoading]);

  // Effect 2: Reset list + page when search text or clinic/clinician changes
  useEffect(() => {
    isFetchingRef.current = false;
    setChatPatients([]);
    setPage(1);
    if (clinicianId && clinicId) {
      dispatch(getChatListingLogic({
        page: 1,
        page_size: String(PAGE_SIZE),
        search: filterText,
        clinic_id: clinicId,
        clinician_id: clinicianId,
      }));
    }
  }, [filterText, clinicId, clinicianId, dispatch]);

  // Effect 3: Fetch the next page when page increments (infinite scroll only)
  useEffect(() => {
    if (page <= 1) return; // page 1 is handled by Effect 2
    if (!clinicianId || !clinicId) return;
    dispatch(getChatListingLogic({
      page,
      page_size: String(PAGE_SIZE),
      search: filterText,
      clinic_id: clinicId,
      clinician_id: clinicianId,
    }));
  }, [page]); // intentionally only page — search resets handled by Effect 2

  useEffect(() => {
    if (isEmptyObject(latestMessage)) {
      return;
    }
    setChatPatients((prevPatients) => {
      if (!prevPatients || prevPatients.length === 0) return prevPatients;
      return updateMessageInSidebar(
        latestMessage?.message,
        latestMessage?.peerID,
        prevPatients,
        latestMessage?.updateBadge,
        chatEnabledPatients,
        clinicianId
      );
    });
  }, [latestMessage, chatEnabledPatients, clinicianId]);

  return (
    <div
      className="col-sm-12 col-md-6 col-lg-6"
      style={{ borderRight: "2px solid lightgray" }}
    >
      <StartChat
        setIsOpen={setModalOpen}
        modalIsOpen={isModalOpen}
        patientData={chatEnabledPatients}
        setChatData={setChatData}
      />
      <div className="d-flex search-icon-container my-3">
        <SearchIcon style={{ marginTop: "2%" }} />
        <input
          placeholder="Find a chat"
          onChange={(e) => {
            setPage(1);
            setFilterText(e.target.value);
          }}
          value={filterText}
        />
        <button
          className="btn transparent-btn"
          title="Start a new chat!"
          onClick={() => {
            setModalOpen(true);
          }}
        >
          <PlusCircleIcon fill="#4aa7e0" style={{ marginTop: "2%" }} />
        </button>
      </div>

      {chatLoading && page === 1 ? (
        <PatientLoader />
      ) : chatPatients && chatPatients.filter(Boolean).length > 0 ? (
        <div 
          className="mb-3 chat-list" 
          onScroll={(e) => {
            const bottom = e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight < 50;
            if (bottom && !isFetchingRef.current && hasMore) {
              isFetchingRef.current = true;
              setPage((prevPage) => prevPage + 1);
            }
          }}
          style={{ overflowY: "auto", maxHeight: "calc(100vh - 200px)" }}
        >
          {chatPatients.filter(Boolean).map((chat, index) => {
            return (
              <ChatTile
                key={index}
                onClick={() => {
                  if (
                    sessionStorage.getItem("openChatID") ===
                    chat?.patient_id?.toString()
                  ) {
                    return;
                  }
                  setChatData(chat);
                  dispatch(getUnreadMessagesLogic());
                }}
                avatar={chat?.profile_picture}
                name={chat?.name}
                // Prefer live count from Agora, then API unread_count, then fallback UnreadPatientsChatCount
                count={chat?.count !== undefined ? chat?.count : (chat?.unread_count !== undefined ? chat?.unread_count : (UnreadPatientsChatCount[chat?.patient_id] || 0))}
                // date={chat?.modified}
                previewText={(chat?.latest_message || "").replace(/\[.*?\]/g, '').trim()}
                risk={chat?.flag}
              />
            );
          })}
          {chatLoading && page > 1 && (
            <div className="text-center my-3">
              <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></span>
              <span className="ms-2">Loading more...</span>
            </div>
          )}
        </div>
      ) : (
        <div className="mb-3 chat-list">
          <NoMessage
            callBack={() => {
              setModalOpen(true);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ChatListing;
